import { Capacitor } from '@capacitor/core';
import { Amplify } from 'aws-amplify';

import { EcommerceConfig } from './interfaces';
import { isAndroidApp } from './utils/platformUtils';
import { isRemix } from './utils/ssrUtils';

const isNativePlatform = Capacitor.isNativePlatform();

// The next hack is because AWS Amplify v6 won't accept
// non-http/s as a redirect_uri (i.e. com.simlocal.esim.travel:/).
// This is because they added a React-native specific package and plugin,
// but since we're using Capacitor it won't work with it.
// So the hack intercepts the token request and changes the
// redirect_uri before it's executed.
// AWSOAuthWorkaroundPlugin is also part of this hack.
// They removed the urlOpener parameter in v6, and within
// that plugin we're intercepting, setting the redirect_uri
// and opening with Capacitor.Browser as we did for v5.
// See here https://github.com/aws-amplify/amplify-js/issues/10301
// and many other related issues at https://github.com/aws-amplify/amplify-js/issues?q=is%3Aissue%20state%3Aopen%20capacitor
if (isNativePlatform) {
  const originalFetch = window.fetch;

  window.fetch = async (
    input: RequestInfo | URL,
    init?: RequestInit,
  ): Promise<Response> => {
    const reqUrl = input.toString();
    if (
      init?.method === 'POST' &&
      typeof init.body === 'string' &&
      reqUrl.startsWith('https://oauth') &&
      reqUrl.endsWith('/oauth2/token')
    ) {
      const originalBody = init.body;
      const updatedBody = originalBody.replace(
        isAndroidApp
          ? 'redirect_uri=https%3A%2F%2Fshop.simlocal.com'
          : 'redirect_uri=capacitor%3A%2F%2Fshop.simlocal.com',
        'redirect_uri=com.simlocal.esim.travel%3A%2F',
      );

      const newInit = {
        ...init,
        body: updatedBody,
      };

      return originalFetch(input, newInit);
    }
    return await originalFetch(input, init);
  };
}

export const getAmplifyConfigureObject = (config: EcommerceConfig) => {
  const region = config.COGNITO_REGION;
  const userPoolId = config.COGNITO_USER_POOL_ID;
  const userPoolClientId = config.COGNITO_USER_POOL_WEB_CLIENT_ID;
  const oauthDomain = config.COGNITO_OAUTH_DOMAIN;

  return {
    Auth: {
      Cognito: {
        region,
        userPoolId,
        userPoolClientId,
        ...(oauthDomain
          ? {
              loginWith: {
                oauth: {
                  domain: oauthDomain,
                  responseType: 'code' as 'code' | 'token',
                  scopes: [
                    'profile',
                    'openid',
                    'email',
                    'aws.cognito.signin.user.admin',
                  ],
                  redirectSignIn:
                    typeof window === 'undefined'
                      ? []
                      : [window.location.origin],
                  redirectSignOut:
                    typeof window === 'undefined'
                      ? []
                      : [window.location.origin],
                },
              },
            }
          : {}),
      },
    },
  };
};

export const configureAmplify = (config: EcommerceConfig) => {
  Amplify.configure(getAmplifyConfigureObject(config), { ssr: isRemix() });
};
